// Kiracı provizyonu onayı — POST /ops/v1/tenants/{id}/provision (spec 62).
// Uç VAR OLAN bir kiracı satırına veritabanı + varsayılanları bağlar; yeni
// kiracı yaratmaz, gövde almaz. Çizimdeki "Yeni müşteri" sihirbazının
// (panel/PagesBiz.jsx) şirket/plan/bölge/ilk-yönetici adımlarını yazan bir uç
// yok, bu yüzden eylem detay sayfasında tek onaylı bir düğmedir.
//
// Uç idempotency anahtarı almıyor; tekrar çağrı sunucuda fail-closed (zaten
// provizyonlu kiracı benzersiz indekse çarpar ve "failed" döner). Bu yüzden
// istek sürerken düğme kilitli, sonuç ekranında tekrar düğmesi yok.
//
// Sonucun `last_error` alanı gösterilmez: altyapı/kütüphane hata metnidir
// (CLAUDE.md "kütüphane hata metnini kullanıcıya taşıma"); iş kimliği
// operatörün onu denetim kaydında ve iş tablosunda bulmasına yeter.

import { useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ServerCog } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@novemai/components";

import { unwrap } from "@/api/envelope";
import { operatorGraph } from "@/api/operator-graph";
import type { paths } from "@/api/types.gen";
import { showErrorToast } from "@/lib/error-toast";

type ProvisionData = NonNullable<
  paths["/ops/v1/tenants/{id}/provision"]["post"]["responses"][200]["content"]["application/json"]["data"]
>;

export async function provisionTenant(tenantId: string): Promise<ProvisionData> {
  return unwrap(
    operatorGraph.POST("/ops/v1/tenants/{id}/provision", {
      params: { path: { id: tenantId } },
    }),
  );
}

export function ProvisionTenantModal({
  tenant,
  onClose,
}: {
  readonly tenant: { readonly id: string; readonly name: string };
  readonly onClose: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const titleId = useId();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<ProvisionData | null>(null);

  const mutation = useMutation({
    mutationFn: provisionTenant,
    onSuccess: (data): void => {
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ["operator", "tenant", tenant.id] });
    },
    onError: (error): void => {
      showErrorToast(error, t);
    },
  });

  const close = (): void => {
    if (mutation.isPending) return;
    onClose();
  };

  return (
    <Modal
      open
      onClose={close}
      aria-labelledby={titleId}
      closeOnEscape={!mutation.isPending}
      closeOnScrimClick={false}
    >
      <ModalHeader
        id={titleId}
        title={result === null ? t("provision.title") : t("provision.result_title")}
      />
      {result === null ? (
        <>
          <ModalBody>
            <p className="mb-3 mt-0 font-serif text-[13.5px] leading-relaxed text-[var(--fg-secondary)]">
              {t("provision.body", { tenant: tenant.name })}
            </p>
            <p className="mb-0 mt-0 font-sans text-[12px] text-[var(--fg-muted)]">
              {t("provision.note")}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={close} disabled={mutation.isPending}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                if (!mutation.isPending) mutation.mutate(tenant.id);
              }}
              loading={mutation.isPending}
            >
              <ServerCog size={14} aria-hidden="true" />
              {t("provision.confirm")}
            </Button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalBody>
            <div className="mb-3 flex items-center gap-2">
              <Badge tone={result.terminal_state === "succeeded" ? "success" : "danger"}>
                {t(`provision.state.${result.terminal_state}`)}
              </Badge>
              <span className="font-sans text-[12px] text-[var(--fg-muted)]">
                {t("provision.profiles_cloned", { count: result.profiles_cloned })}
              </span>
            </div>
            <p className="mb-2 mt-0 font-serif text-[13px] leading-relaxed text-[var(--fg-secondary)]">
              {result.terminal_state === "succeeded"
                ? t("provision.succeeded_body")
                : t("provision.failed_body")}
            </p>
            <div className="mb-1 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-muted)]">
              {t("provision.steps_label")}
            </div>
            {result.steps_completed.length === 0 ? (
              <p className="mb-3 mt-0 font-sans text-[12px] text-[var(--fg-muted)]">
                {t("provision.no_steps")}
              </p>
            ) : (
              <ul className="mb-3 mt-0 list-disc pl-5 font-mono text-[11.5px] text-[var(--fg-primary)]">
                {result.steps_completed.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            )}
            <div className="mb-1 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-muted)]">
              {t("provision.job_label")}
            </div>
            <code className="block break-all font-mono text-[11px] text-[var(--fg-secondary)]">
              {result.job_id}
            </code>
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={onClose}>
              {t("common.done")}
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}
